import React from "react";
import { Button, Label } from "reactstrap";
import Select from "react-select";
import LoadingSpinner from "../../Components/Common/Spinner";
import { isDeveloperUser } from "../../helpers/functions/feedbackTool";
import "./feedbackGen.styles.css";

const FeedbackRightSideTools = ({
  selectedColor,
  setSelectedColor,
  isDrawing,
  toggleDrawingMode,
  isAddingText,
  toggleTextMode,
  isAddingRect,
  toggleRectMode,
  isBlurring,
  toggleBlurMode,
  handleClearClick,
  handleDownloadClick,
  feedbackData,
  setFeedbackData,
  handleSendFeedback,
  loadingFeedback,
  toggle,
  onUploadImage,
  onChooseWindow,
  onRetakeSnapshot,
  hasImage,
  recapturing,
}) => {
  const IssueTypeOptions = [
    { label: "Bug", value: "Bug" },
    { label: "Improvement", value: "Improvement" },
    { label: "Prototype", value: "Prototype" },
    { label: "Other", value: "Other" },
  ];

  const AreaAffectedOptions = [
    { label: "Frontend", value: "Frontend" },
    { label: "Backend", value: "Backend" },
    { label: "Both", value: "Both" },
    { label: "Unsure", value: "Unsure" },
  ];

  const SeverityOptions = [
    { label: "Major", value: "Major" },
    { label: "Minor", value: "Minor" },
  ];

  // Task 24733 - the tool is open to every staff member and customer user, but
  // the triage fields are not. "Let AI handle this" (which files the report
  // straight at status 136), "Issue Type", "Area Affected" and the Quick Set
  // shortcuts that fill the last two are internal routing decisions nobody
  // outside development can answer sensibly, so they only render for
  // User.FlagDeveloper. Everyone else gets Title, Description, Severity and the
  // screenshot tools - the backend already guards LetAiHandle on the same flag,
  // so hiding the checkbox is a UI simplification, not the security boundary.
  const isDeveloper = isDeveloperUser();

  // Label the shortcut the way the machine it is running on spells it.
  // navigator.platform is deprecated but is the one thing every browser still
  // agrees on; a wrong guess only mislabels a hint, it does not break the key.
  const isMac = /Mac|iPhone|iPad|iPod/i.test(
    typeof navigator === "undefined" ? "" : navigator.platform || ""
  );

  // Task 24588. Ctrl+Enter (Cmd+Enter on a Mac) sends, so a quick report can be
  // typed and fired without reaching for the mouse.
  //
  // Scoped to this panel rather than the document: the shortcut should only
  // exist while the form has focus, not hijack Ctrl+Enter everywhere in the
  // app. The Title input carries autoFocus, so focus is already inside.
  //
  // Guarded on loadingFeedback for the same reason the footer swaps the buttons
  // for a spinner - holding the keys down must not fire a second report.
  // Validation is not repeated here: handleSendFeedback already refuses without
  // a Title, so the shortcut and the button behave identically.
  const handlePanelKeyDown = (event) => {
    if (event.key !== "Enter") return;
    if (!event.ctrlKey && !event.metaKey) return;
    if (loadingFeedback) return;
    event.preventDefault();
    handleSendFeedback();
  };

  return (
    <div
      className={"right-side-full-screen-modal"}
      onKeyDown={handlePanelKeyDown}
    >
      <div className="d-flex flex-column h-100">
        <div className="feedback-tool-header mb-2">
          <h5 className="mb-0">Feedback Report</h5>
        </div>

        <div className="feedback-tool-body flex-grow-1 overflow-auto">
          {/* Task 24510 - Title and Description first.
              They used to sit below ten other controls: the Feedback Tools
              heading, re-capture, upload, the colour picker and five annotation
              buttons. Title is the ONLY required field, so the fastest possible
              capture meant scrolling past everything optional to reach the one
              box that has to be filled in. Now it is the first thing under the
              heading, and everything else has moved below it. */}
          <div className="p-2">
            <div className="mb-2">
              <Label>
                Title <span className="text-danger">*</span>
              </Label>
              <input
                className="form-control"
                placeholder="What went wrong?"
                type="text"
                autoFocus
                value={feedbackData.Title}
                onChange={(e) =>
                  setFeedbackData({ ...feedbackData, Title: e.target.value })
                }
              />
            </div>

            <div className="mb-2">
              <Label>Description</Label>
              <textarea
                className="form-control"
                placeholder="Anything else worth knowing (optional)"
                rows={3}
                value={feedbackData.Description}
                onChange={(e) =>
                  setFeedbackData({
                    ...feedbackData,
                    Description: e.target.value,
                  })
                }
              />
            </div>

            <small className="text-muted d-block">
              That is enough to send. Everything below is optional.
            </small>
          </div>

          <div>
            <hr />
            <h6>Screenshot &amp; annotation</h6>
          </div>

          {/* Task 24511 - the screenshot controls. "Re-capture / choose window"
              is the browser's screen-share picker, which does not exist in iOS
              Safari, so it is only rendered when the parent says the device
              actually has it. "Retake snapshot" reruns the same DOM capture the
              bug button uses and works everywhere, iPhone included. */}
          <div className="mb-3">
            <Label className="fw-semibold small mb-1">Screenshot</Label>

            {!hasImage && (
              <div className="alert alert-warning py-2 px-2 small mb-2">
                No screenshot attached. Retake it, or upload one from your
                device below. You can still send the report without one.
              </div>
            )}

            {onRetakeSnapshot && (
              <Button
                color="primary"
                outline
                size="sm"
                className="w-100 mb-2"
                onClick={onRetakeSnapshot}
                disabled={recapturing}
              >
                {recapturing ? "Capturing…" : "Retake snapshot of this page"}
              </Button>
            )}

            {onChooseWindow && (
              <Button
                color="secondary"
                outline
                size="sm"
                className="w-100"
                onClick={onChooseWindow}
                disabled={recapturing}
              >
                {recapturing
                  ? "Waiting for selection…"
                  : "Choose a different window"}
              </Button>
            )}

            <small className="text-muted d-block mt-1">
              {onChooseWindow
                ? "Defaults to this window. Use the second button to pick another window or the whole screen."
                : "Captures the page behind this form. Your phone's own screenshot can be attached below."}
            </small>
          </div>

          {/* Upload image / photo - NO capture attribute, so iOS offers photo library / files */}
          <div className="mb-3">
            <Label className="fw-semibold small mb-1">
              Upload Screenshot / Photo
            </Label>
            <input
              type="file"
              accept="image/*"
              className="form-control form-control-sm"
              onChange={onUploadImage}
            />
            <small className="text-muted">
              Or upload your own screenshot, then annotate below.
            </small>
          </div>

          {/* Colour + tools */}
          <div>
            <div className="d-flex align-items-center mb-2">
              <input
                className="ms-2 me-2"
                style={{ width: "25px", height: "25px" }}
                type="color"
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
              />
              <span>Select Color</span>
            </div>

            <div className="d-flex align-items-center mb-2">
              <Button
                color="primary"
                onClick={toggleDrawingMode}
                className="mx-2 w-100"
              >
                {isDrawing ? "Stop Drawing" : "Draw"}
              </Button>
            </div>

            <div className="d-flex align-items-center mb-2">
              <Button
                color="secondary"
                onClick={toggleTextMode}
                className="mx-2 w-100"
              >
                {isAddingText ? "Stop Adding Text" : "Add Text"}
              </Button>
            </div>

            <div className="d-flex align-items-center mb-2">
              <Button
                color="warning"
                onClick={toggleRectMode}
                className="mx-2 w-100"
              >
                {isAddingRect ? "Stop Adding Rectangle" : "Add Rectangle"}
              </Button>
            </div>

            <div className="d-flex align-items-center mb-2">
              <Button
                color="dark"
                onClick={toggleBlurMode}
                className="mx-2 w-100"
              >
                {isBlurring ? "Stop Blurring" : "Blur (Hide Data)"}
              </Button>
            </div>

            <div className="d-flex align-items-center mb-2">
              <Button
                color="danger"
                onClick={handleClearClick}
                className="mx-2 w-100"
              >
                Clear
              </Button>
            </div>

            <div className="d-flex align-items-center mb-2">
              <Button
                color="success"
                onClick={handleDownloadClick}
                className="mx-2 w-100"
              >
                Download
              </Button>
            </div>
          </div>

          <hr />

          {/* Feedback form */}
          <div className="p-2">

            {/* Quick-set shortcuts: set Issue Type + Area Affected in one click.
                Developer-only, along with the two fields it fills. */}
            {isDeveloper && (
            <div className="mb-2">
              <Label>Quick Set</Label>
              <div className="d-flex gap-2">
                <Button
                  color="info"
                  outline
                  size="sm"
                  className="w-100"
                  onClick={() =>
                    setFeedbackData({
                      ...feedbackData,
                      IssueType: "Improvement",
                      AreaAffected: "Frontend",
                    })
                  }
                >
                  Improvement Frontend
                </Button>
                <Button
                  color="info"
                  outline
                  size="sm"
                  className="w-100"
                  onClick={() =>
                    setFeedbackData({
                      ...feedbackData,
                      IssueType: "Bug",
                      AreaAffected: "Frontend",
                    })
                  }
                >
                  Bug Frontend
                </Button>
              </div>
            </div>
            )}

            {isDeveloper && (
            <div className="mb-2">
              <Label>Issue Type</Label>
              <Select
                options={IssueTypeOptions}
                value={
                  IssueTypeOptions.find(
                    (o) => o.value === feedbackData.IssueType
                  ) || null
                }
                onChange={(e) =>
                  setFeedbackData({ ...feedbackData, IssueType: e.value })
                }
              />
            </div>
            )}

            {isDeveloper && (
            <div className="mb-2">
              <Label>Area Affected</Label>
              <Select
                options={AreaAffectedOptions}
                value={
                  AreaAffectedOptions.find(
                    (o) => o.value === feedbackData.AreaAffected
                  ) || null
                }
                onChange={(e) =>
                  setFeedbackData({ ...feedbackData, AreaAffected: e.value })
                }
              />
            </div>
            )}

            <div className="mb-2">
              <Label>Severity</Label>
              <Select
                options={SeverityOptions}
                value={
                  SeverityOptions.find(
                    (o) => o.value === feedbackData.Severity
                  ) || null
                }
                onChange={(e) =>
                  setFeedbackData({ ...feedbackData, Severity: e.value })
                }
              />
            </div>

            {/* Developer-only shortcut: queue the report straight for AI dev. */}
            {isDeveloper && (
              <div className="mb-2">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="letAiHandle"
                    checked={!!feedbackData.LetAiHandle}
                    onChange={(e) =>
                      setFeedbackData({
                        ...feedbackData,
                        LetAiHandle: e.target.checked,
                      })
                    }
                  />
                  <label
                    className="form-check-label fw-semibold"
                    htmlFor="letAiHandle"
                  >
                    Let AI handle this
                  </label>
                </div>
                <small className="text-muted d-block mt-1">
                  Developer only. When ticked, this report is created with task
                  status 136 (queued for AI development) instead of the normal
                  bug-triage queue.
                </small>
              </div>
            )}
          </div>
        </div>

        {/* Footer buttons */}
        <div className="p-2 feedback-tool-footer">
          {loadingFeedback ? (
            <LoadingSpinner size={"small"} row={true} />
          ) : (
            <div className="d-flex justify-content-end align-items-center">
              {/* Task 24588 - a shortcut nobody is told about is a shortcut
                  nobody uses. */}
              <small className="text-muted me-auto">
                <kbd>{isMac ? "⌘" : "Ctrl"}</kbd> + <kbd>Enter</kbd> to send
              </small>
              <Button className="btn btn-danger me-2" onClick={toggle}>
                Close
              </Button>
              <Button
                onClick={handleSendFeedback}
                className="btn btn-success"
                title={`Send feedback (${isMac ? "⌘" : "Ctrl"} + Enter)`}
              >
                Send Feedback
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackRightSideTools;
